import { useEffect, type ReactNode } from 'react';
import { ALL_PAGES, TOOL_PAGES } from '../content/catalogue.js';
import { GUIDE_PAGES, TRUST_PAGES } from '../content/docs.js';
import { Logo } from '../ui/icons.js';

/**
 * Shared chrome for every CONTENT page (plan §12.2).
 *
 * Deliberately not used by the editor: /app is a different security zone with
 * its own full-height layout, and sharing a header between them would drag
 * content-page code into the isolated bundle. The two stay visually one
 * product through the token layer in `styles.css`, not through shared JSX.
 *
 * `title` and `description` are applied to the document head on hydration for
 * client-side navigation. The version search engines read is written into the
 * static HTML by `scripts/prerender.mjs`, straight from the content registry —
 * an effect would be far too late for a crawler.
 */
export interface LayoutProps {
  readonly title: string;
  readonly description: string;
  readonly canonical: string;
  /**
   * Keep this page out of search results.
   *
   * A share link must never be indexed: the URL contains the id, and an
   * indexed one would be a publicly listed pointer to someone's confidential
   * file. `_headers` marks `/s/*` noindex at the HTTP level too — this is the
   * client-rendered half of the same rule.
   */
  readonly noindex?: boolean;
  readonly children: ReactNode;
}

/** Split the footer's tool list into balanced columns without a CSS hack. */
function columns<T>(items: readonly T[], count: number): T[][] {
  const per = Math.ceil(items.length / count);
  return Array.from({ length: count }, (_, i) => items.slice(i * per, (i + 1) * per));
}

export function Layout({ title, description, canonical, noindex, children }: LayoutProps) {
  // Keeps the tab title correct after an in-page navigation. The prerendered
  // HTML already carries the right values on first load.
  useEffect(() => {
    document.title = title;
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute('content', description);
    const link = document.querySelector('link[rel="canonical"]');
    // A page with no canonical URL of its own must not inherit the previous
    // page's — that would point a share link at an indexable tool page.
    if (link) {
      if (canonical) link.setAttribute('href', canonical);
      else link.removeAttribute('href');
    }

    let robots = document.querySelector('meta[name="robots"]');
    if (noindex && !robots) {
      robots = document.createElement('meta');
      robots.setAttribute('name', 'robots');
      document.head.appendChild(robots);
    }
    if (robots) robots.setAttribute('content', noindex ? 'noindex, nofollow' : 'index, follow');
  }, [title, description, canonical, noindex]);

  return (
    <div className="flex min-h-screen flex-col bg-canvas text-ink">
      {/* Sticky and translucent so long tool pages keep the editor one click
          away without a solid bar cutting the page in half. */}
      <header className="sticky top-0 z-50 border-b border-line/70 bg-canvas/80 backdrop-blur-xl">
        <div className="shell flex h-14 items-center gap-3">
          <a href="/" className="group flex items-center gap-2.5">
            <Logo className="h-7 w-7 rounded-lg" />
            <span className="text-[0.9375rem] font-620 tracking-tight">PrivatePDF</span>
          </a>

          <span
            aria-hidden
            className="ml-1 hidden h-4 w-px bg-line sm:block"
          />
          <p className="hidden text-[0.8125rem] text-faint sm:block">
            Nothing uploaded, ever
          </p>

          <nav className="ml-auto flex items-center gap-1">
            <a href="/#tools" className="btn btn-ghost btn-sm hidden sm:inline-flex">
              All tools
            </a>
            <a href="/app" className="btn btn-primary btn-sm">
              Open the editor
            </a>
          </nav>
        </div>
      </header>

      <div className="flex-1">{children}</div>

      <footer className="mt-24 border-t border-line bg-surface/50">
        <div className="shell py-14">
          <div className="grid gap-10 md:grid-cols-[minmax(0,1.1fr)_minmax(0,2fr)]">
            <div>
              <div className="flex items-center gap-2.5">
                <Logo className="h-7 w-7 rounded-lg" />
                <span className="font-620 tracking-tight">PrivatePDF</span>
              </div>
              <p className="mt-4 max-w-xs text-sm leading-relaxed text-muted">
                Every tool runs inside your browser. Your document is never
                uploaded — which is a property of how this is built, not a line
                in a policy.
              </p>
              <div className="status-strip mt-5 w-fit">
                <span aria-hidden className="dot dot-ok" />
                <span>0 bytes uploaded</span>
              </div>
            </div>

            <div>
              <h2 className="eyebrow">All tools</h2>
              <div className="mt-4 grid gap-x-8 gap-y-2 sm:grid-cols-3">
                {columns(ALL_PAGES, 3).map((column, i) => (
                  <ul key={i} className="space-y-2">
                    {column.map((page) => (
                      <li key={page.slug}>
                        <a
                          href={`/${page.slug}`}
                          className={`text-sm transition-colors hover:text-ink ${
                            'text-muted'
                          }`}
                        >
                          {page.title}
                        </a>
                      </li>
                    ))}
                  </ul>
                ))}
              </div>
            </div>
          </div>

          {/*
            * The evidence behind the claim in the paragraph above.
            *
            * A site that says "verify it, don't trust it" and gives the reader
            * nowhere to verify is asking for the trust it disclaims. These
            * documents existed from Phase 0 and were unreachable until now.
            */}
          <nav aria-label="Trust" className="mt-10 border-t border-line pt-6">
            <h2 className="eyebrow">How to check us</h2>
            <ul className="mt-3 flex flex-wrap gap-x-6 gap-y-2">
              {TRUST_PAGES.map((doc) => (
                <li key={doc.slug}>
                  <a
                    href={`/${doc.slug}`}
                    className="text-sm text-muted transition-colors hover:text-ink"
                  >
                    {doc.navLabel}
                  </a>
                </li>
              ))}
            </ul>
          </nav>
          <nav aria-label="Conversions we do not offer" className="mt-6">
            <h2 className="eyebrow">Not offered, and why</h2>
            <ul className="mt-3 flex flex-wrap gap-x-6 gap-y-2">
              {GUIDE_PAGES.map((doc) => (
                <li key={doc.slug}>
                  <a
                    href={`/${doc.slug}`}
                    className="text-sm text-muted transition-colors hover:text-ink"
                  >
                    {doc.navLabel}
                  </a>
                </li>
              ))}
            </ul>
          </nav>

          <div className="mt-8 flex flex-col gap-3 border-t border-line pt-6 text-xs text-faint sm:flex-row sm:items-center">
            <p>
              {TOOL_PAGES.length} tools available · runs offline · no account,
              no size limit
            </p>
            <p className="font-mono-ui sm:ml-auto">Open the network tab. We&rsquo;ll wait.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
